import { css } from '@emotion/react';
import {
  Banner,
  Button,
  color,
  FormGroup,
  Icon,
  Input,
  spacing,
  Typography
} from '@uniquegood/realworld-studio-design';
import { useState } from 'react';
import { ListStyleTypeDisc } from '@/styles';

interface DeleteProjectModalProps {
  projectName: string;
  onDelete: () => void;
  onCancel: () => void;
}

function DeleteProjectModal({ projectName, onDelete, onCancel }: DeleteProjectModalProps) {
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const isDeleteEnabled = deleteConfirmInput === projectName;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.common.large};
      `}
    >
      <Typography type="body">
        &apos;{projectName}&apos;이 완전히 삭제되며, 삭제된 이후에는 복구할 수 없어요.
        <br />
        정말 콘텐츠를 삭제하려면 콘텐츠 이름을 입력해 주세요.
      </Typography>
      <FormGroup label="콘텐츠 이름 입력">
        <Input
          placeholder={projectName}
          value={deleteConfirmInput}
          onChange={(value) => setDeleteConfirmInput(value)}
        />
      </FormGroup>
      <Banner
        header={
          <Typography type="heading" color="critical">
            삭제 전, 꼭 확인해 주세요!
          </Typography>
        }
        iconComponent={<Icon size="20px" icon="exclamation_circle_solid" color="#D53D40" />}
        cssStyle={css`
          > div {
            background-color: ${color.surface_critical_subdued} !important;
            border-color: ${color.border_critical_subdued} !important;
          }
        `}
      >
        <ul
          css={[
            ListStyleTypeDisc,
            css`
              margin: 0;
              padding-left: ${spacing.padding.large};
              font-size: 14px;
              line-height: 20px;
              color: ${color.text_default};

              li {
                margin-bottom: ${spacing.margin.xsmall};

                &:last-child {
                  margin-bottom: 0;
                }
              }
            `
          ]}
        >
          <li>삭제 즉시 더이상 채널과 리얼월드 앱에서 볼 수 없습니다.</li>
          <li>콘텐츠 제작시 사용했던 AI 크레딧은 환불되지 않습니다.</li>
          <li>이 콘텐츠를 편집중인 사람이 있는 경우 작업이 중단됩니다.</li>
        </ul>
      </Banner>
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding-top: ${spacing.padding.large};
          padding-left: ${spacing.padding.xlarge2};
          padding-right: ${spacing.padding.xlarge2};
          margin-top: ${spacing.margin.large};
          margin-left: -${spacing.padding.xlarge2};
          margin-right: -${spacing.padding.xlarge2};
          border-top: 1px solid ${color.border_default_subdued};
        `}
      >
        <Button onClick={onCancel} size="large">
          취소
        </Button>
        <Button
          type="destructive"
          size="large"
          disabled={!isDeleteEnabled}
          onClick={onDelete}
          cssStyle={
            isDeleteEnabled
              ? css`
                  background-color: #e56767;
                  color: ${color.white};

                  &:hover {
                    background-color: #d53d40;
                  }
                `
              : undefined
          }
        >
          콘텐츠 삭제
        </Button>
      </div>
    </div>
  );
}

export default DeleteProjectModal;
